<script>
import axios from '@/utils/axios';

export default {
  inject: ['setLoading', 'auth'],
  data() {
    return {
      file: null,
      fileName: '',
      importing: false,
      importId: null,
      progress: null,   // full progress object
      pollTimer: null,
    };
  },
  computed: {
    pct() {
      return this.progress ? parseFloat(this.progress.progress_percentage) || 0 : 0;
    },
    isDone() {
      return this.progress && this.progress.status === 'completed';
    },
  },
  beforeUnmount() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    onFileChange(e) {
      const f = e.target.files[0];
      if (!f) return;
      this.file = f;
      this.fileName = f.name;
      this.progress = null;
      this.importId = null;
    },

    async startImport() {
      if (!this.file) {
        this.$swalToast('Please choose a CSV file first.', 'error');
        return;
      }
      this.importing = true;
      this.progress = null;
      try {
        const fd = new FormData();
        fd.append('file', this.file);
        fd.append('import_type', 'attendances');

        const res = await axios.post('/csv/import', fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        this.importId = res.data.data?.import_id || res.data.data?.importId;
        if (this.importId) {
          this.pollProgress();
        } else {
          this.$swalToast('Import started but no tracking id returned.', 'error');
          this.importing = false;
        }
      } catch (error) {
        this.$swalToast(error.message, 'error');
        this.importing = false;
      }
    },

    pollProgress() {
      if (this.pollTimer) clearInterval(this.pollTimer);
      this.fetchProgress();
      this.pollTimer = setInterval(this.fetchProgress, 1500);
    },

    async fetchProgress() {
      if (!this.importId) return;
      try {
        const res = await axios.get(`/csv/progress/${this.importId}`);
        this.progress = res.data.data;
        if (this.progress.status === 'completed' || this.progress.status === 'failed') {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
          this.importing = false;
        }
      } catch (error) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
        this.importing = false;
        this.$swalToast(error.message, 'error');
      }
    },

    reset() {
      this.file = null;
      this.fileName = '';
      this.progress = null;
      this.importId = null;
      this.importing = false;
      if (this.pollTimer) clearInterval(this.pollTimer);
    },
  },
};
</script>

<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">

      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <div class="d-flex align-items-center gap-2">
          <a href="javascript:void(0);" class="btn btn-icon btn-light" @click="$router.push('/hrms/attendances')"><i class="ti ti-arrow-left"></i></a>
          <h4 class="page-title mb-0">Import Attendance (CSV)</h4>
        </div>
      </div>

      <div class="row">
        <div class="col-lg-7">

          <!-- Upload card -->
          <div class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
            <div class="card-body p-4">
              <div class="imp-note mb-3">
                <i class="ti ti-info-circle me-1"></i>
                Upload a CSV with columns: <strong>employee_code, date, punch_in, punch_out, remarks</strong>.
                Dates and times must match the expected format.
              </div>

              <label class="imp-drop" :class="{ 'has-file': fileName }">
                <input type="file" accept=".csv" @change="onFileChange" hidden />
                <i class="ti" :class="fileName ? 'ti-file-check' : 'ti-cloud-upload'" style="font-size:30px;"></i>
                <span v-if="fileName" class="imp-fname">{{ fileName }}</span>
                <span v-else class="imp-hint">Click to choose a CSV file</span>
              </label>

              <div class="d-flex gap-2 mt-3">
                <button class="btn btn-primary" :disabled="!file || importing" @click="startImport">
                  <span v-if="importing" class="spinner-border spinner-border-sm me-1"></span>
                  {{ importing ? 'Importing...' : 'Start Import' }}
                </button>
                <button class="btn btn-light" :disabled="importing" @click="reset">Reset</button>
              </div>
            </div>
          </div>

          <!-- Progress card -->
          <div v-if="progress" class="card border-0 shadow-sm mb-3" style="border-radius:14px;">
            <div class="card-body p-4">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="mb-0 fw-bold" style="font-size:13px;">Import Progress</h6>
                <span class="imp-status" :class="isDone ? 'imp-done' : 'imp-running'">{{ progress.status }}</span>
              </div>

              <div class="imp-bar mb-2">
                <div class="imp-bar-fill" :style="{ width: pct + '%' }"></div>
              </div>
              <div style="font-size:11px; color:#64748b;">{{ pct }}% — {{ progress.processed_rows }}/{{ progress.total_rows }} rows</div>

              <!-- summary -->
              <div class="d-flex gap-3 mt-3">
                <div class="imp-stat">
                  <span class="imp-stat-num text-success">{{ progress.successful_rows }}</span>
                  <span class="imp-stat-lbl">Success</span>
                </div>
                <div class="imp-stat">
                  <span class="imp-stat-num text-danger">{{ progress.failed_rows }}</span>
                  <span class="imp-stat-lbl">Failed</span>
                </div>
                <div class="imp-stat">
                  <span class="imp-stat-num">{{ progress.total_rows }}</span>
                  <span class="imp-stat-lbl">Total</span>
                </div>
              </div>

              <!-- errors -->
              <div v-if="progress.errors && progress.errors.length" class="mt-3">
                <h6 class="fw-bold mb-2" style="font-size:12px; color:#dc2626;">
                  <i class="ti ti-alert-triangle me-1"></i>Row Errors ({{ progress.errors.length }})
                </h6>
                <div class="imp-err" v-for="(e, i) in progress.errors" :key="i">
                  <span class="imp-err-row">Row {{ e.row }}</span>
                  <span class="imp-err-msg">{{ e.error }}</span>
                  <span v-if="e.data" class="imp-err-data">{{ e.data.employee_code }} · {{ e.data.date }}</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

    </div>
    <app-footer></app-footer>
  </div>
</template>

<style scoped>
.imp-note {
  font-size: 11.5px; color: #475569;
  background: #f8fafc; border: 1px solid #e2e8f0;
  border-radius: 8px; padding: 10px 12px; line-height: 1.6;
}
.imp-drop {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; padding: 32px; cursor: pointer;
  border: 2px dashed #cbd5e1; border-radius: 12px; color: #94a3b8;
  transition: all .15s ease;
}
.imp-drop:hover { border-color: #6366f1; color: #6366f1; background: #f8faff; }
.imp-drop.has-file { border-color: #16a34a; color: #16a34a; background: #f0fdf4; }
.imp-fname { font-size: 13px; font-weight: 600; }
.imp-hint { font-size: 12px; }

.imp-status { font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 20px; text-transform: capitalize; }
.imp-running { background: #fef3c7; color: #b45309; }
.imp-done { background: #dcfce7; color: #16a34a; }

.imp-bar { height: 8px; background: #f1f5f9; border-radius: 20px; overflow: hidden; }
.imp-bar-fill { height: 100%; background: #6366f1; border-radius: 20px; transition: width .3s ease; }

.imp-stat { display: flex; flex-direction: column; }
.imp-stat-num { font-size: 18px; font-weight: 700; color: #0f172a; }
.imp-stat-lbl { font-size: 10px; color: #94a3b8; text-transform: uppercase; }

.imp-err {
  display: flex; align-items: center; gap: 8px; flex-wrap: wrap;
  font-size: 11px; padding: 7px 10px; border-radius: 7px;
  background: #fef2f2; margin-bottom: 5px;
}
.imp-err-row { font-weight: 700; color: #dc2626; }
.imp-err-msg { color: #475569; }
.imp-err-data { color: #94a3b8; margin-left: auto; }
</style>